import { generateVOProformaPdf } from "./VOProformaPDF";
import React, { useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useDebouncedValue } from "./useDebouncedValue";
import { tokens } from "./theme";
import SectionFilter from "./SectionFilter";
import { matchesSection } from "../utils/sections";
import {
  Card as UICard,
  BlueprintGrid,
  Reveal,
  Eyebrow,
  Input as UIInput,
  Select as UISelect,
  Textarea,
  FormRow,
  FormGroupDivider,
  Button as UIButton,
  Badge as UIBadge,
  Checkbox as UICheckbox,
  Stat,
  Table as UITable,
  Th as UITh,
  Td as UITd,
  Empty,
} from "./ui";
import { requiresSignoff, approvalBadge, approveDocument } from "../utils/approvals";
import VersionHistory from "./VersionHistory";
import { scanVariationDocument } from "../utils/aiScan";

const T = tokens;

// Human labels for scanner-fillable fields — used in the review banner.
const VO_SCAN_LABELS = {
  variationNo: "VO Number", requestDate: "Date Requested", voDescription: "VO Description",
  motivation: "Motivation", reason: "Reason", requestedBy: "Requested By",
  fundingSource: "Funding Source", status: "Status", employer: "Employer", contractor: "Contractor",
  estimatedAdditionalDays: "Est. Additional Time", additionalWorkAmount: "Additional Work Amount",
  existingRatesAmount: "Existing Rates Amount", engineerRef: "Engineer Ref", contractorRef: "Contractor Ref",
  instructionRef: "Instruction Ref", drawingRef: "Drawing Ref", specificationRef: "Specification Ref",
  clauseRef: "Clause Ref", initiatedBy: "Initiated By", programmeRef: "Programme Ref",
  paymentCertificateRef: "Payment Certificate Ref", dateInstructed: "Date Instructed",
  dateSubmitted: "Date Submitted", dateAssessed: "Date Assessed", dateApproved: "Date Approved",
  costCategory: "Cost Category", estimatedValue: "Estimated Value", quotedValue: "Quoted Value",
  assessedValue: "Assessed Value", approvedValue: "Approved Value",
  amountPreviouslyCertified: "Amount Previously Certified", amountCertifiedThisPeriod: "Amount Certified This Period",
  eotClaimedDays: "EOT Claimed Days", eotAssessedDays: "EOT Assessed Days", eotApprovedDays: "EOT Approved Days",
  criticalPathImpact: "Critical Path Impact", notes: "Notes",
};

const DS = {
  blue: "#1D6FE8",
  blueSoft: "#E8F0FD",
  teal: "#0E8C6A",
  tealSoft: "#E5F5F0",
  red: "#C9352A",
  redSoft: "#FBE9E8",
  muted: "#64748B",
  border: "#E2E8F2",
  surface: "#F8FAFC",
  white: "#FFFFFF",
  text: "#0F172A",
  r8: "8px",
  r12: "12px",
};

function numberOrZero(value) {
  if (value === null || value === undefined || value === "") return 0;

  let cleaned = String(value).trim();
  cleaned = cleaned.replace(/[^\d,.-]/g, "");

  if (cleaned.includes(",") && !cleaned.includes(".")) {
    cleaned = cleaned.replace(",", ".");
  } else {
    cleaned = cleaned.replace(/,/g, "");
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function round2(value) {
  return Math.round(numberOrZero(value) * 100) / 100;
}

function currency(value) {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numberOrZero(value));
}

function normalizeItemNo(value) {
  return String(value || "").trim().toUpperCase();
}

function isApprovedVariation(item) {
  return String(item?.status || "").trim().toLowerCase() === "approved";
}

function statusBadgeVariant(status) {
  if (status === "Approved") return "success";
  if (status === "Rejected" || status === "Cancelled") return "danger";
  if (status === "Draft") return "accent";
  if (status === "Included in PC" || status === "Closed" || status === "Implemented") return "info";
  return "primary";
}

function getAllocatedOmissionQtyForItem(itemNo, variations = [], currentVariationId = "") {
  const normalized = normalizeItemNo(itemNo);
  if (!normalized || !Array.isArray(variations)) return 0;

  return variations
    .filter((v) => v && String(v.id || "") !== String(currentVariationId || ""))
    .filter((v) => isApprovedVariation(v))
    .reduce((sum, v) => {
      const items = Array.isArray(v.omissionItems) ? v.omissionItems : [];
      return sum + items
        .filter((e) => e && normalizeItemNo(e.itemNo) === normalized)
        .reduce((innerSum, e) => innerSum + numberOrZero(e.omitQty), 0);
    }, 0);
}

function getCertifiedQtyForItem(itemNo, certificates = []) {
  const normalized = normalizeItemNo(itemNo);
  if (!normalized || !Array.isArray(certificates)) return 0;

  return certificates
    .filter(Boolean)
    .flatMap((cert) =>
      [
        cert.claimedItems,
        cert.soqItems,
        cert.certificateItems,
        cert.claimedSoqItems,
        cert.items,
      ].filter(Array.isArray).flat()
    )
    .filter((e) => e && normalizeItemNo(e.itemNo || e.itemNumber || e.soqItemNo) === normalized)
    .reduce((sum, e) => {
      const directQty = numberOrZero(
        e.claimQty ?? e.claimedQty ?? e.certifiedQty ?? e.qtyThisCertificate ??
        e.quantityThisCertificate ?? e.quantityThisMonth ?? e.qtyThisMonth ??
        e.quantity ?? e.qty ?? e.finalQty ?? 0
      );

      if (directQty > 0) return sum + directQty;

      const amount = numberOrZero(e.amountThisMonth ?? e.certifiedAmount ?? e.claimedAmount ?? e.amount ?? e.value ?? 0);
      const rate = numberOrZero(e.tenderRate ?? e.rate ?? e.unitRate ?? e.finalRate ?? 0);
      return rate > 0 ? sum + amount / rate : sum;
    }, 0);
}

function getAvailableOmissionQty(item, variations = [], certificates = [], currentVariationId = "") {
  try {
    const itemNo = item?.itemNo;
    if (!itemNo) return 0;

    const raw = numberOrZero(item?.tenderQty) -
      getCertifiedQtyForItem(itemNo, certificates) -
      getAllocatedOmissionQtyForItem(itemNo, variations, currentVariationId);

    return Math.max(0, round2(raw));
  } catch (error) {
    console.warn("Could not calculate available omission qty", error, { item, currentVariationId });
    return 0;
  }
}

function getCertifiedBreakdownForVariation(variationRow, certificates = []) {
  if (!variationRow) return [];

  const voId = String(variationRow.id || "").trim();
  const voNo = String(variationRow.variationNo || "").trim().toLowerCase();
  const result = [];

  for (const cert of Array.isArray(certificates) ? certificates : []) {
    if (!cert) continue;
    const extraWorkItems = cert.extraWorkItems || cert.variationItems || cert.voItems || [];
    let certAmount = 0;

    for (const entry of Array.isArray(extraWorkItems) ? extraWorkItems : []) {
      const entryId = String(entry?.variationId || "").trim();
      const entryNo = String(entry?.variationNo || "").trim().toLowerCase();
      const matches = (voId && entryId === voId) || (voNo && entryNo === voNo);

      if (matches) {
        certAmount += numberOrZero(entry.amountThisCertificate ?? entry.amount ?? entry.certifiedAmount ?? 0);
      }
    }

    if (certAmount !== 0) {
      const certNo = String(
        cert.certificateNo || cert.certificateNumber ||
        cert.paymentCertificateNo || cert.pcNo || cert.id || "—"
      ).trim();

      result.push({
        certNo,
        certDate: cert.certificateDate || cert.date || cert.issueDate || "",
        amount: round2(certAmount),
      });
    }
  }

  if (result.length === 0) {
    const prev = numberOrZero(variationRow.amountPreviouslyCertified);
    const curr = numberOrZero(variationRow.amountCertifiedThisPeriod);
    const ref = variationRow.paymentCertificateRef || variationRow.certificateNo || "";
    if (prev > 0) result.push({ certNo: "Previously certified", certDate: "", amount: prev });
    if (curr > 0) result.push({ certNo: ref || "This period", certDate: "", amount: curr });
  }

  return result;
}

function rowTotalCertified(variationRow, certificates = []) {
  if (!variationRow) return 0;
  const breakdown = getCertifiedBreakdownForVariation(variationRow, certificates);
  if (breakdown.length > 0) return breakdown.reduce((s, b) => s + numberOrZero(b.amount), 0);
  return numberOrZero(variationRow.amountPreviouslyCertified) + numberOrZero(variationRow.amountCertifiedThisPeriod);
}

const STATUS_OPTIONS = [
  "Draft", "Instructed", "Awaiting Quotation", "Under Assessment",
  "Recommended", "Approved", "Rejected", "Implemented",
  "Included in PC", "Closed", "Cancelled",
];

const COST_CATEGORY_OPTIONS = [
  "addition", "omission", "substitution", "remeasurement", "provisional adjustment",
];

const REQUESTED_BY_OPTIONS = ["Employer", "Engineer", "Contractor"];
const FUNDING_SOURCE_OPTIONS = ["Savings", "Contingency", "Section 116"];

const emptyVariationForm = {
  projectId: "",
  variationNo: "",
  requestDate: "",
  voDescription: "",
  estimatedAdditionalDays: "",
  motivation: "",
  reason: "Additional Work",
  requestedBy: "Employer",
  fundingSource: "Contingency",
  employer: "",
  engineerRef: "",
  contractorRef: "",
  contractor: "",
  title: "",
  description: "",
  instructionRef: "",
  rfiRef: "",
  boqSection: "",
  boqSectionId: "",
  boqSectionNo: "",
  boqSectionName: "",
  boqSectionLabel: "",
  boqSectionReference: "",
  drawingRef: "",
  specificationRef: "",
  clauseRef: "",
  initiatedBy: "Engineer",
  dateInstructed: "",
  dateSubmitted: "",
  dateAssessed: "",
  dateApproved: "",
  costCategory: "addition",
  estimatedValue: "",
  quotedValue: "",
  assessedValue: "",
  approvedValue: "",
  status: "Draft",
  eotClaimedDays: "",
  eotAssessedDays: "",
  eotApprovedDays: "",
  criticalPathImpact: "No",
  programmeRef: "",
  paymentCertificateRef: "",
  amountPreviouslyCertified: "",
  amountCertifiedThisPeriod: "",
  contractContingenciesAmount: "",
  submittedVariationOrdersAmount: "",
  totalVariationOrdersAmount: "",
  originalDueCompletionDate: "",
  currentDueCompletionDate: "",
  projectedDueCompletionDate: "",
  additionalWorkAmount: "",
  existingRatesAmount: "",
  omittedWorkAmount: "",
  omissionItems: [],
  netVoAmount: "",
  notes: "",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: `1px solid ${DS.border}`,
  borderRadius: DS.r8,
  padding: "8px 10px",
  fontSize: 13,
  color: DS.text,
  background: DS.white,
  fontFamily: "inherit",
};

const scrollCSS = `
  .variation-section-shell,
  .variation-section-shell *,
  .vo-form-modal,
  .vo-form-modal * {
    box-sizing: border-box;
  }

  .variation-section-shell {
    position: relative;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: clip;
    font-family: inherit;
  }

  .variation-content-stack {
    position: relative;
    z-index: 1;
    display: grid;
    gap: 18px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  /* Grid items default to min-width:auto, which lets the wide (1850px) register
     table refuse to shrink and stretch the whole stack — clipping the right edge
     of every card (incl. the "New Variation Order" button). Clamp every direct
     child so the table's scroll container is the thing that scrolls, not the page. */
  .variation-content-stack > * {
    min-width: 0;
    max-width: 100%;
  }

  .variation-card-contained {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .variation-kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .variation-kpi-grid > div,
  .variation-kpi-grid * {
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .variation-responsive-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
    gap: 10px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .variation-table-scroll {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;
  }

  .variation-table-inner {
    width: 1850px;
    min-width: 1850px;
    max-width: none;
  }

  .variation-table-inner table {
    width: 1850px;
    table-layout: auto;
  }

  .variation-table-inner th:nth-child(2),
  .variation-table-inner td:nth-child(2) {
    position: sticky;
    left: 36px;
    z-index: 3;
    background: #FFFFFF;
    box-shadow: 10px 0 16px rgba(15, 23, 42, 0.06);
  }

  .variation-table-inner thead th:nth-child(2) {
    z-index: 4;
  }

  .variation-table-inner tfoot td:nth-child(2) {
    background: #F8FAFC;
  }

  .variation-table-inner th,
  .variation-table-inner td {
    white-space: nowrap;
  }

  .variation-table-inner td:nth-child(4),
  .variation-table-inner th:nth-child(4) {
    white-space: normal;
    min-width: 220px;
    max-width: 260px;
  }

  @media (max-width: 1200px) {
    .variation-kpi-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 760px) {
    .variation-kpi-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
`;

const OmissionItemRow = React.memo(function OmissionItemRow({
  item,
  selectedEntry,
  isChecked,
  isBlocked,
  availableQty,
  certifiedQty,
  allocatedQty,
  calculatedOmitted,
  grossVoAmount,
  onToggle,
  onQtyChange,
}) {
  const isOverAllocated = isChecked && selectedEntry && numberOrZero(selectedEntry.omitQty) > availableQty;
  const otherOmitted = calculatedOmitted - numberOrZero(selectedEntry?.omittedAmount);
  const netRemaining = Math.max(0, grossVoAmount - otherOmitted);
  const rate = numberOrZero(item.tenderRate);
  const rawFill = rate > 0 ? Math.min(availableQty, netRemaining / rate) : 0;
  const fillQty = rate > 0 ? Math.ceil(rawFill * 100) / 100 : 0;
  const canFill = isChecked && availableQty > 0 && grossVoAmount > 0 && fillQty > 0;
  const alreadyFilled = canFill && Math.abs(round2(numberOrZero(selectedEntry?.omitQty)) - fillQty) < 0.01;

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "28px minmax(0,1fr) 130px 36px 130px",
      gap: 10,
      alignItems: "center",
      padding: "10px 14px",
      borderBottom: `1px solid ${DS.border}`,
      background: isBlocked || isOverAllocated ? DS.redSoft : isChecked ? DS.blueSoft : DS.white,
    }}>
      <input type="checkbox" checked={isChecked} onChange={(e) => onToggle(item, e.target.checked)} style={{ accentColor: DS.blue }} />
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 12 }}>
          {item.itemNo} — {item.description || "No description"}
          {item.identifiedSaving && (
            <span style={{ marginLeft: 8, padding: "1px 6px", borderRadius: 4, background: DS.tealSoft, color: DS.teal, fontSize: 10, fontWeight: 700 }}>
              SAVING
            </span>
          )}
        </p>
        <p style={{ margin: "2px 0 0", fontSize: 11, color: DS.muted }}>
          {item.sectionNo} {item.sectionName} | {item.unit || "—"} | Rate {currency(item.tenderRate)} | Tender qty {numberOrZero(item.tenderQty)} | Certified {certifiedQty} | Other VOs {allocatedQty} | <strong>Available {availableQty}</strong>
        </p>
        {isOverAllocated && <p style={{ margin: "3px 0 0", fontSize: 11, color: DS.red, fontWeight: 700 }}>⚠ Exceeds available balance — reduce quantity</p>}
      </div>
      <input
        type="number"
        min="0"
        max={availableQty}
        placeholder="Omit qty"
        disabled={!isChecked}
        value={selectedEntry?.omitQty || ""}
        onChange={(e) => onQtyChange(item, e.target.value)}
        style={{ ...inputStyle, background: isOverAllocated ? DS.redSoft : DS.white, borderColor: isOverAllocated ? DS.red : DS.border }}
      />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
        <input
          type="checkbox"
          title={canFill ? `Auto-fill ${fillQty} to zero net VO` : "Select item first"}
          disabled={!canFill}
          checked={alreadyFilled}
          onChange={(e) => onQtyChange(item, e.target.checked ? String(fillQty) : "")}
          style={{ accentColor: DS.teal, width: 15, height: 15, cursor: canFill ? "pointer" : "default" }}
        />
        <span style={{ fontSize: 9, color: canFill ? DS.teal : DS.muted, fontWeight: 700, lineHeight: 1 }}>→0</span>
      </div>
      <div style={{ padding: "8px 10px", borderRadius: DS.r8, fontSize: 12, fontWeight: 700, background: isChecked && numberOrZero(selectedEntry?.omitQty) > 0 ? DS.tealSoft : DS.surface, border: `1px solid ${DS.border}`, color: DS.text, textAlign: "right" }}>
        {currency(selectedEntry?.omittedAmount || 0)}
      </div>
    </div>
  );
});

function CertifiedInfoPopover({ breakdown }) {
  const [open, setOpen] = useState(false);

  if (!breakdown || breakdown.length === 0) return <span style={{ color: DS.muted, fontSize: 11 }}>—</span>;

  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 4 }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="View certificate breakdown"
        style={{
          background: DS.blueSoft,
          border: "1px solid #BDD3F9",
          borderRadius: "50%",
          width: 18,
          height: 18,
          fontSize: 10,
          fontWeight: 800,
          color: DS.blue,
          cursor: "pointer",
          lineHeight: 1,
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        i
      </button>

      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 999 }} onClick={() => setOpen(false)} />
          <div style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            right: 0,
            zIndex: 1000,
            background: DS.white,
            border: `1px solid ${DS.border}`,
            borderRadius: DS.r12,
            boxShadow: "0 8px 24px rgba(15,23,42,0.13)",
            minWidth: 290,
            padding: "10px 14px",
          }}>
            <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: DS.muted }}>
              Certified by Certificate
            </p>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Certificate", "Date", "Amount"].map((h) => (
                    <th key={h} style={{ padding: "4px 8px", textAlign: h === "Amount" ? "right" : "left", color: DS.muted, fontWeight: 700, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${DS.border}` }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {breakdown.map((b, i) => (
                  <tr key={`${b.certNo}-${i}`} style={{ borderBottom: `1px solid ${DS.border}` }}>
                    <td style={{ padding: "5px 8px", fontWeight: 600, color: DS.blue }}>{b.certNo}</td>
                    <td style={{ padding: "5px 8px", color: DS.muted }}>{b.certDate || "—"}</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700 }}>{currency(b.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ padding: "5px 8px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: DS.text }}>
                    Total Certified
                  </td>
                  <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 800, color: DS.teal }}>
                    {currency(breakdown.reduce((s, b) => s + b.amount, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </span>
  );
}

// Defined at module level so their identity is stable across renders —
// defining them inside VOFormModal remounted every card on each keystroke.
const CollapseCard = ({ title, subtitle, accent = T.color.primary, collapsed, onToggle, children }) => (
  <div style={{ position: "relative", background: T.color.surface, border: `1px solid ${T.color.border}`, borderRadius: 12, overflow: "hidden", boxShadow: T.shadow.sm }}>
    <div style={{ position: "absolute", top: 0, left: 0, width: 3, bottom: collapsed ? "auto" : 0, height: collapsed ? "100%" : "auto", background: accent }} />
    <button
      type="button"
      onClick={onToggle}
      style={{ width: "100%", textAlign: "left", border: "none", background: "none", padding: "16px 20px 14px 24px", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderBottom: collapsed ? "none" : `1px solid ${T.color.border}` }}
    >
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h4 style={{ margin: 0, fontFamily: T.font.display, fontSize: 14, fontWeight: 700, letterSpacing: "-0.015em", color: T.color.ink }}>{title}</h4>
          {collapsed && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>Collapsed</span>}
        </div>
        {subtitle && <p style={{ margin: "3px 0 0", fontSize: 11.5, color: T.color.inkMuted, lineHeight: 1.4 }}>{subtitle}</p>}
      </div>
      <span style={{ fontSize: 14, color: T.color.inkMuted, transform: collapsed ? "rotate(0deg)" : "rotate(180deg)" }}>▾</span>
    </button>
    {!collapsed && <div style={{ padding: "16px 20px 20px 24px" }}>{children}</div>}
  </div>
);

const ReadOnlyCalc = ({ value, accent }) => (
  <div style={{ padding: "8px 12px", height: 36, background: accent ? T.color.accentSoft : T.color.surfaceAlt, border: `1px solid ${accent ? "#F5BFA3" : T.color.border}`, borderRadius: 6, fontSize: 13, fontWeight: 700, color: accent ? T.color.accent : T.color.ink, display: "flex", alignItems: "center", fontFamily: T.font.mono, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
    {value}
  </div>
);

function VOFormModal({
  initialForm,
  editingVariationId,
  projects = [],
  rfis = [],
  boqSections = [],
  soqItems = [],
  variations = [],
  certificates = [],
  onSave,
  onClose,
}) {
  const [form, setForm] = useState(initialForm || emptyVariationForm);
  const set = useCallback((patch) => setForm((prev) => ({ ...prev, ...patch })), []);

  // ── Upload & Scan — read a completed VO document and pre-fill the form ───────
  const scanInputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // { name, fields:[keys] } | null
  const [scanError, setScanError] = useState("");
  const [scanKey, setScanKey] = useState(0); // bump to remount uncontrolled inputs

  async function handleScanFile(e) {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setScanError("");
    setScanResult(null);
    setScanning(true);
    try {
      const found = await scanVariationDocument(file);
      // Select-bound fields must match an allowed option (case-insensitively);
      // anything else is dropped so we never set an invalid dropdown value.
      const enums = {
        status: STATUS_OPTIONS,
        requestedBy: REQUESTED_BY_OPTIONS,
        fundingSource: FUNDING_SOURCE_OPTIONS,
        costCategory: COST_CATEGORY_OPTIONS,
        criticalPathImpact: ["Yes", "No"],
      };
      const clean = {};
      for (const [k, v] of Object.entries(found)) {
        const opts = enums[k];
        if (opts) {
          const match = opts.find((o) => o.toLowerCase() === String(v).toLowerCase());
          if (match) clean[k] = match;
        } else {
          clean[k] = v;
        }
      }
      const keys = Object.keys(clean);
      if (!keys.length) {
        setScanError("No variation fields could be read from that document.");
        return;
      }
      setForm((prev) => ({ ...prev, ...clean }));
      setScanResult({ name: file.name, fields: keys });
      setScanKey((k) => k + 1); // force uncontrolled (defaultValue) inputs to refresh
    } catch (err) {
      setScanError(err?.message || "Could not scan that document.");
    } finally {
      setScanning(false);
    }
  }

  const [collapseHeader, setCollapseHeader] = useState(false);
  const [collapseValuation, setCollapseValuation] = useState(false);
  const [collapseAdmin, setCollapseAdmin] = useState(true);
  const [omissionSearch, setOmissionSearch] = useState("");
  const [omissionShowFilter, setOmissionShowFilter] = useState("All");
  const [omissionSavingsFilter, setOmissionSavingsFilter] = useState("All");
  const [omissionSectionFilter, setOmissionSectionFilter] = useState("All");
  const [omissionSortField, setOmissionSortField] = useState("itemNo");
  const [omissionSortDir, setOmissionSortDir] = useState("asc");
  const [omissionError, setOmissionError] = useState("");
  const [blockedItemNo, setBlockedItemNo] = useState("");

  const additionalWorkAmount = numberOrZero(form.additionalWorkAmount);
  const existingRatesAmount = numberOrZero(form.existingRatesAmount);
  const omissionItems = Array.isArray(form.omissionItems) ? form.omissionItems : [];
  const calculatedOmitted = omissionItems.reduce((s, e) => s + numberOrZero(e.omittedAmount), 0);
  const grossVoAmount = additionalWorkAmount + existingRatesAmount;
  const netVoAmount = grossVoAmount - calculatedOmitted;
  const approvedValue = numberOrZero(form.approvedValue || grossVoAmount);
  const balanceToCertify = approvedValue - numberOrZero(form.amountPreviouslyCertified) - numberOrZero(form.amountCertifiedThisPeriod);

  const projectRfis = (Array.isArray(rfis) ? rfis : []).filter((r) => r.projectId === form.projectId);

  const projectSoqItems = useMemo(() =>
    (Array.isArray(soqItems) ? soqItems : [])
      .filter((i) => i.projectId === form.projectId)
      .sort((a, b) => String(a.itemNo || "").localeCompare(String(b.itemNo || ""), undefined, { numeric: true, sensitivity: "base" })),
    [soqItems, form.projectId]
  );

  const projectBoqSections = useMemo(() =>
    (Array.isArray(boqSections) ? boqSections : [])
      .filter((i) => i.projectId === form.projectId)
      .sort((a, b) => String(a.sectionNo || "").localeCompare(String(b.sectionNo || ""), undefined, { numeric: true, sensitivity: "base" })),
    [boqSections, form.projectId]
  );

  const selectedBoqSectionValue = form.boqSectionId || projectBoqSections.find((i) => String(i.sectionName || "") === String(form.boqSection || ""))?.id || "";

  const setOmissionItems = useCallback((nextItems) => {
    const priced = (Array.isArray(nextItems) ? nextItems : []).map((entry) => {
      const src = projectSoqItems.find((i) => normalizeItemNo(i.itemNo) === normalizeItemNo(entry.itemNo));
      const rate = numberOrZero(src?.tenderRate ?? entry?.tenderRate);
      const qty = numberOrZero(entry.omitQty);
      return { ...entry, tenderRate: rate, omittedAmount: round2(qty * rate) };
    });

    const rawTotal = priced.reduce((s, e) => s + numberOrZero(e.omittedAmount), 0);
    const cappedTotal = round2(Math.min(rawTotal, grossVoAmount));
    setForm((prev) => ({ ...prev, omissionItems: priced, omittedWorkAmount: String(cappedTotal) }));
  }, [projectSoqItems, grossVoAmount]);

  const handleToggleOmission = useCallback((sourceItem, isChecked) => {
    const itemNo = normalizeItemNo(sourceItem?.itemNo);
    if (!itemNo) return;

    if (!isChecked) {
      setOmissionItems(omissionItems.filter((e) => normalizeItemNo(e.itemNo) !== itemNo));
      return;
    }

    if (omissionItems.some((e) => normalizeItemNo(e.itemNo) === itemNo)) return;

    setOmissionItems([
      ...omissionItems,
      {
        itemNo: sourceItem.itemNo || "",
        paymentRef: sourceItem.paymentRef || "",
        description: sourceItem.description || "",
        unit: sourceItem.unit || "",
        tenderQty: numberOrZero(sourceItem.tenderQty),
        omitQty: "",
        tenderRate: numberOrZero(sourceItem.tenderRate),
        omittedAmount: 0,
        sectionNo: sourceItem.sectionNo || "",
        sectionName: sourceItem.sectionName || "",
      },
    ]);
  }, [omissionItems, setOmissionItems]);

  const handleOmissionQtyChange = useCallback((sourceItem, value) => {
    const requested = round2(numberOrZero(value));
    const available = getAvailableOmissionQty(sourceItem, variations, certificates, editingVariationId);

    if (requested > available) {
      setBlockedItemNo(sourceItem.itemNo || "");
      setOmissionError(`${sourceItem.itemNo} only has ${available} available. You tried to allocate ${requested}.`);
      return;
    }

    setBlockedItemNo("");
    setOmissionError("");
    setOmissionItems(omissionItems.map((e) =>
      normalizeItemNo(e.itemNo) === normalizeItemNo(sourceItem.itemNo) ? { ...e, omitQty: value } : e
    ));
  }, [omissionItems, variations, certificates, editingVariationId, setOmissionItems]);

  const filteredOmissionItems = useMemo(() =>
    projectSoqItems
      .filter((item) => {
        const selected = omissionItems.some((e) => normalizeItemNo(e.itemNo) === normalizeItemNo(item.itemNo));
        if (omissionShowFilter === "Selected" && !selected) return false;
        if (omissionShowFilter === "Unselected" && selected) return false;
        if (omissionSavingsFilter === "savings" && !item.identifiedSaving) return false;
        if (omissionSavingsFilter === "non-savings" && item.identifiedSaving) return false;
        if (!matchesSection(item, omissionSectionFilter)) return false;
        const hay = [item.itemNo, item.paymentRef, item.description, item.sectionNo, item.sectionName].join(" ").toLowerCase();
        return !omissionSearch.trim() || hay.includes(omissionSearch.toLowerCase());
      })
      .sort((a, b) => {
        const aSelected = omissionItems.some((e) => normalizeItemNo(e.itemNo) === normalizeItemNo(a.itemNo));
        const bSelected = omissionItems.some((e) => normalizeItemNo(e.itemNo) === normalizeItemNo(b.itemNo));
        let cmp = 0;

        if (omissionSortField === "selected") cmp = Number(bSelected) - Number(aSelected);
        else if (omissionSortField === "description") cmp = String(a.description || "").localeCompare(String(b.description || ""));
        else if (omissionSortField === "section") cmp = (`${a.sectionNo}${a.sectionName}`).localeCompare(`${b.sectionNo}${b.sectionName}`);
        else if (omissionSortField === "availableQty") cmp = getAvailableOmissionQty(a, variations, certificates, editingVariationId) - getAvailableOmissionQty(b, variations, certificates, editingVariationId);
        else if (omissionSortField === "omitQty") {
          const ae = omissionItems.find((e) => normalizeItemNo(e.itemNo) === normalizeItemNo(a.itemNo));
          const be = omissionItems.find((e) => normalizeItemNo(e.itemNo) === normalizeItemNo(b.itemNo));
          cmp = numberOrZero(ae?.omitQty) - numberOrZero(be?.omitQty);
        } else {
          cmp = String(a.itemNo || "").localeCompare(String(b.itemNo || ""), undefined, { numeric: true, sensitivity: "base" });
        }

        return omissionSortDir === "asc" ? cmp : -cmp;
      }),
    [projectSoqItems, omissionItems, omissionSearch, omissionShowFilter, omissionSavingsFilter, omissionSectionFilter, omissionSortField, omissionSortDir, variations, certificates, editingVariationId]
  );

  function handleSave() {
    const invalidItem = omissionItems.find((entry) => {
      const src = projectSoqItems.find((i) => normalizeItemNo(i.itemNo) === normalizeItemNo(entry.itemNo)) || entry;
      return numberOrZero(entry.omitQty) > getAvailableOmissionQty(src, variations, certificates, editingVariationId);
    });

    if (invalidItem) {
      setOmissionError(`${invalidItem.itemNo || "This item"} exceeds the available balance. Reduce the VO quantity before saving.`);
      return;
    }

    setOmissionError("");

    const finalGross = round2(grossVoAmount);
    const finalOmitted = round2(calculatedOmitted);
    const finalNet = round2(netVoAmount);
    const finalApproved = numberOrZero(form.approvedValue) > 0 ? numberOrZero(form.approvedValue) : finalGross;
    const isNowApproved = String(form.status || "").trim() === "Approved";

    onSave({
      ...form,
      status: form.status || "Draft",
      grossVoAmount: String(finalGross),
      paymentCertificateVoAmount: String(finalGross),
      approvedValue: String(round2(finalApproved)),
      dateApproved: isNowApproved && !form.dateApproved ? new Date().toISOString().slice(0, 10) : form.dateApproved || "",
      omittedWorkAmount: String(finalOmitted),
      netVoAmount: String(finalNet),
      proformaNetVoAmount: String(finalNet),
      omissionItems,
    });
  }

  return createPortal(
    <div className="vo-form-modal" style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(14,27,44,0.62)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 16px", overflowY: "auto", fontFamily: T.font.body }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(920px, 98vw)", background: T.color.canvas, borderRadius: 14, boxShadow: "0 32px 80px rgba(14,17,22,0.32), 0 2px 0 rgba(255,255,255,0.04) inset", border: `1px solid ${T.color.border}`, overflow: "hidden", marginBottom: 24, position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${T.color.accent} 0%, ${T.color.primary} 40%, ${T.color.primary} 100%)`, zIndex: 11 }} />

        <div style={{ padding: "20px 24px", background: T.color.surface, borderBottom: `1px solid ${T.color.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, position: "sticky", top: 0, zIndex: 10, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <Eyebrow color={T.color.accent}>{editingVariationId ? "Editing Variation" : "New Variation Order"}</Eyebrow>
              {form.variationNo && <span style={{ fontFamily: T.font.mono, fontSize: 11, fontWeight: 600, color: T.color.inkMuted, letterSpacing: "0.02em" }}>{form.variationNo}</span>}
              {form.status && <UIBadge variant={statusBadgeVariant(form.status)} size="sm">{form.status}</UIBadge>}
            </div>
            <h3 style={{ margin: 0, fontFamily: T.font.display, fontSize: 18, fontWeight: 700, letterSpacing: "-0.025em", color: T.color.ink }}>{form.voDescription || form.title || "Untitled Variation"}</h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: T.color.inkMuted, lineHeight: 1.4 }}>Fill in the core details below. Internal admin fields are collapsed by default.</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input ref={scanInputRef} type="file" accept=".pdf,image/*" style={{ display: "none" }} onChange={handleScanFile} />
            <UIButton variant="primary" size="sm" disabled={scanning} onClick={() => scanInputRef.current && scanInputRef.current.click()}>
              {scanning ? "⏳ Scanning…" : "⬆ Upload & Scan"}
            </UIButton>
            <UIButton variant="subtle" size="sm" onClick={() => setForm({ ...emptyVariationForm, projectId: form.projectId })}>Clear</UIButton>
            <button onClick={onClose} aria-label="Close" style={{ background: T.color.surfaceAlt, border: `1px solid ${T.color.border}`, cursor: "pointer", color: T.color.inkMuted, fontSize: 16, lineHeight: 1, padding: "8px 12px", borderRadius: 6, fontFamily: "inherit", fontWeight: 600 }}>✕</button>
          </div>
        </div>

        {(scanResult || scanError) && (
          <div style={{ padding: "12px 24px 0" }}>
            {scanError ? (
              <div style={{ padding: "12px 16px", borderRadius: 10, background: T.color.dangerSoft, border: `1px solid ${T.color.dangerBorder}`, color: T.color.danger, fontSize: 13, fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <span>⚠ {scanError}</span>
                <UIButton variant="subtle" size="sm" onClick={() => setScanError("")}>Dismiss</UIButton>
              </div>
            ) : (
              <div style={{ padding: "12px 16px", borderRadius: 10, background: T.color.successSoft, border: `1px solid ${T.color.successBorder}`, borderLeft: `3px solid ${T.color.success}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.color.ink }}>
                    ✓ Scanned {scanResult.fields.length} field{scanResult.fields.length === 1 ? "" : "s"} from {scanResult.name}
                  </span>
                  <UIButton variant="subtle" size="sm" onClick={() => setScanResult(null)}>Dismiss</UIButton>
                </div>
                <p style={{ margin: "0 0 6px", fontSize: 12, color: T.color.inkMuted, lineHeight: 1.5 }}>
                  Review the values below — especially amounts, dates and the omission picker — then save. Nothing is committed until you do.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {scanResult.fields.map((k) => (
                    <UIBadge key={k} variant="success" size="sm">{VO_SCAN_LABELS[k] || k}</UIBadge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div key={`vo-scan-${scanKey}`} style={{ padding: "20px 24px", display: "grid", gap: 14 }}>
          <CollapseCard title="VO Details" subtitle="Core identification and commercial details" accent={T.color.primary} collapsed={collapseHeader} onToggle={() => setCollapseHeader((v) => !v)}>
            <FormRow label="Project *" helper="Project this variation belongs to">
              <UISelect value={form.projectId} onChange={(e) => set({ projectId: e.target.value })}>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.id} — {p.name}</option>)}
              </UISelect>
            </FormRow>
            <FormRow label="VO Number *" helper="Formal variation order reference"><UIInput mono defaultValue={form.variationNo} onBlur={(e) => set({ variationNo: e.target.value })} /></FormRow>
            <FormRow label="Date Requested" helper="Date the variation was requested or instructed"><UIInput type="date" value={form.requestDate} onChange={(e) => set({ requestDate: e.target.value })} /></FormRow>
            <FormRow label="VO Description *" helper="Short descriptive title for the variation"><UIInput defaultValue={form.voDescription} onBlur={(e) => set({ voDescription: e.target.value })} /></FormRow>
            <FormRow label="Motivation" helper="Detailed motivation for why the variation is required"><Textarea rows={3} defaultValue={form.motivation} onBlur={(e) => set({ motivation: e.target.value })} /></FormRow>
            <FormRow label="Reason for Variation" helper="Short reason category"><UIInput defaultValue={form.reason} onBlur={(e) => set({ reason: e.target.value })} /></FormRow>
            <FormRow label="Requested By" helper="Party that initiated the variation">
              <UISelect value={form.requestedBy} onChange={(e) => set({ requestedBy: e.target.value })}>{REQUESTED_BY_OPTIONS.map((o) => <option key={o}>{o}</option>)}</UISelect>
            </FormRow>
            <FormRow label="Funding Source" helper="Where the commercial value will draw from">
              <UISelect value={form.fundingSource || "Contingency"} onChange={(e) => set({ fundingSource: e.target.value })}>{FUNDING_SOURCE_OPTIONS.map((o) => <option key={o}>{o}</option>)}</UISelect>
            </FormRow>
            <FormRow label="Status" helper="Approval workflow status">
              <UISelect value={form.status} onChange={(e) => set({ status: e.target.value })}>{STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}</UISelect>
            </FormRow>
            <FormRow label="Employer" helper="Employer / client name"><UIInput defaultValue={form.employer} onBlur={(e) => set({ employer: e.target.value })} /></FormRow>
            <FormRow label="Estimated Additional Time" helper="Working days claimed for this VO"><UIInput type="number" defaultValue={form.estimatedAdditionalDays} onBlur={(e) => set({ estimatedAdditionalDays: e.target.value })} /></FormRow>
          </CollapseCard>

          <CollapseCard title="Valuation Summary" subtitle="VO amounts, omission item picker, and net VO calculation" accent={T.color.success} collapsed={collapseValuation} onToggle={() => setCollapseValuation((v) => !v)}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 18, padding: 14, background: T.color.surfaceAlt, borderRadius: 10, border: `1px solid ${T.color.border}` }}>
              {[
                ["Additional Work", currency(additionalWorkAmount)],
                ["PC VO Amount (Gross)", currency(grossVoAmount)],
                ["Omitted Work", currency(calculatedOmitted)],
                ["Net VO (Proforma)", currency(netVoAmount)],
              ].map(([label, value]) => (
                <div key={label} style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>{label}</div>
                  <div style={{ marginTop: 6, fontFamily: T.font.mono, fontSize: 16, fontWeight: 700, color: T.color.ink }}>{value}</div>
                </div>
              ))}
            </div>

            <FormRow label="Additional Work Amount" helper="Cost of additional work performed"><UIInput type="number" defaultValue={form.additionalWorkAmount} onBlur={(e) => set({ additionalWorkAmount: e.target.value })} /></FormRow>
            <FormRow label="Existing Rates Amount" helper="Adjustments made at existing tender rates"><UIInput type="number" defaultValue={form.existingRatesAmount} onBlur={(e) => set({ existingRatesAmount: e.target.value })} /></FormRow>
            <FormRow label="PC VO Amount (Gross)" helper="Auto-calculated: Additional Work + Existing Rates"><ReadOnlyCalc value={currency(grossVoAmount)} accent /></FormRow>

            <FormRow label="Omitted Work BOQ Section" helper="Link omissions to a specific BOQ section">
              <UISelect value={selectedBoqSectionValue} onChange={(e) => {
                const sel = projectBoqSections.find((i) => i.id === e.target.value);
                if (!sel) {
                  set({ boqSection: "", boqSectionId: "", boqSectionNo: "", boqSectionName: "", boqSectionLabel: "", boqSectionReference: "" });
                  return;
                }
                set({
                  boqSection: sel.sectionName || "",
                  boqSectionId: sel.id || "",
                  boqSectionNo: sel.sectionNo || "",
                  boqSectionName: sel.sectionName || "",
                  boqSectionLabel: `${sel.sectionNo} - ${sel.sectionName}`.trim(),
                  boqSectionReference: `${sel.sectionNo} ${sel.sectionName}`.trim(),
                });
              }}>
                <option value="">Not linked</option>
                {projectBoqSections.map((i) => <option key={i.id} value={i.id}>{i.sectionNo} — {i.sectionName}</option>)}
              </UISelect>
            </FormRow>

            <FormRow label="Omitted Work Items" helper="Select SOQ items to omit, enter quantities. Allocations are validated against certified and other approved VOs.">
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, alignItems: "end" }}>
                  <UIInput placeholder="Search items…" value={omissionSearch} onChange={(e) => setOmissionSearch(e.target.value)} />
                  <UISelect value={omissionShowFilter} onChange={(e) => setOmissionShowFilter(e.target.value)}><option value="All">All items</option><option value="Selected">Selected only</option><option value="Unselected">Unselected only</option></UISelect>
                  <UISelect value={omissionSavingsFilter} onChange={(e) => setOmissionSavingsFilter(e.target.value)}><option value="All">All items</option><option value="savings">Savings only ✓</option><option value="non-savings">Non-savings</option></UISelect>
                  <SectionFilter items={projectSoqItems} value={omissionSectionFilter} onChange={setOmissionSectionFilter} />
                  <UISelect value={omissionSortField} onChange={(e) => setOmissionSortField(e.target.value)}><option value="selected">Selected first</option><option value="itemNo">Item number</option><option value="description">Description</option><option value="section">Section</option><option value="availableQty">Available qty</option><option value="omitQty">Omit qty</option></UISelect>
                  <UISelect value={omissionSortDir} onChange={(e) => setOmissionSortDir(e.target.value)}><option value="asc">Ascending</option><option value="desc">Descending</option></UISelect>
                </div>

                <div style={{ maxHeight: 360, overflow: "auto", border: `1px solid ${T.color.border}`, borderRadius: 8, background: T.color.surface }}>
                  {projectSoqItems.length === 0 ? (
                    <div style={{ padding: 24, fontSize: 13, color: T.color.inkMuted, textAlign: "center" }}>No SOQ items imported for this project yet.</div>
                  ) : filteredOmissionItems.length === 0 ? (
                    <div style={{ padding: 24, fontSize: 13, color: T.color.inkMuted, textAlign: "center" }}>No items match the current filter.</div>
                  ) : filteredOmissionItems.map((item) => {
                    const selectedEntry = omissionItems.find((e) => normalizeItemNo(e.itemNo) === normalizeItemNo(item.itemNo));
                    const isChecked = Boolean(selectedEntry);
                    const availableQty = getAvailableOmissionQty(item, variations, certificates, editingVariationId);
                    const certifiedQty = getCertifiedQtyForItem(item.itemNo, certificates);
                    const allocatedQty = getAllocatedOmissionQtyForItem(item.itemNo, variations, editingVariationId);
                    const isBlocked = normalizeItemNo(blockedItemNo) === normalizeItemNo(item.itemNo);

                    return (
                      <OmissionItemRow
                        key={item.id || item.itemNo}
                        item={item}
                        selectedEntry={selectedEntry}
                        isChecked={isChecked}
                        isBlocked={isBlocked}
                        availableQty={availableQty}
                        certifiedQty={certifiedQty}
                        allocatedQty={allocatedQty}
                        calculatedOmitted={calculatedOmitted}
                        grossVoAmount={grossVoAmount}
                        onToggle={handleToggleOmission}
                        onQtyChange={handleOmissionQtyChange}
                      />
                    );
                  })}
                </div>

                {omissionError && <div style={{ padding: "10px 14px", borderRadius: 8, background: T.color.dangerSoft, color: T.color.danger, border: `1px solid ${T.color.dangerBorder}`, fontWeight: 600, fontSize: 12 }}>{omissionError}</div>}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderRadius: 8, background: T.color.surfaceAlt, border: `1px solid ${T.color.border}`, borderLeft: `3px solid ${T.color.success}` }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.color.ink }}>Total Omitted Work Amount</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: T.color.success, fontFamily: T.font.mono }}>{currency(calculatedOmitted)}</span>
                </div>
              </div>
            </FormRow>

            <FormRow label="Net VO Amount (Proforma)" helper="Used for the client VO proforma only. Certificates use the gross amount above."><ReadOnlyCalc value={currency(netVoAmount)} /></FormRow>
          </CollapseCard>

          <CollapseCard title="Internal Admin & EOT Fields" subtitle="References, approval chain, valuations, EOT and payment certificate data" accent={T.color.warning} collapsed={collapseAdmin} onToggle={() => setCollapseAdmin((v) => !v)}>
            <FormGroupDivider>References &amp; Linkage</FormGroupDivider>
            {[
              ["engineerRef", "Engineer Ref", "Internal engineer reference", "text"],
              ["contractorRef", "Contractor Ref", "Contractor reference number", "text"],
              ["contractor", "Contractor", "Contractor name", "text"],
              ["instructionRef", "Instruction Ref", "Site instruction reference", "text"],
              ["drawingRef", "Drawing Ref", "Drawing reference", "text"],
              ["specificationRef", "Specification Ref", "Specification reference", "text"],
              ["clauseRef", "Clause Ref", "Contract clause reference", "text"],
              ["initiatedBy", "Initiated By", "Internal initiator", "text"],
              ["programmeRef", "Programme Ref", "Programme reference", "text"],
              ["paymentCertificateRef", "Payment Certificate Ref", "Linked payment certificate", "text"],
            ].map(([key, label, helper, type]) => (
              <FormRow key={key} label={label} helper={helper}><UIInput mono={key.toLowerCase().includes("ref")} type={type} value={form[key] || ""} onChange={(e) => set({ [key]: e.target.value })} /></FormRow>
            ))}

            <FormRow label="RFI Link" helper="Link this variation to an existing RFI"><UISelect value={form.rfiRef} onChange={(e) => set({ rfiRef: e.target.value })}><option value="">Not linked</option>{projectRfis.map((r) => <option key={r.id} value={r.rfiNo}>{r.rfiNo} — {r.subject}</option>)}</UISelect></FormRow>

            <FormGroupDivider>Workflow Dates</FormGroupDivider>
            {[
              ["dateInstructed", "Date Instructed", "Date the instruction was issued"],
              ["dateSubmitted", "Date Submitted", "Date contractor submitted the quote"],
              ["dateAssessed", "Date Assessed", "Date the engineer assessed the quote"],
              ["dateApproved", "Date Approved", "Date the variation was approved"],
            ].map(([key, label, helper]) => <FormRow key={key} label={label} helper={helper}><UIInput type="date" value={form[key] || ""} onChange={(e) => set({ [key]: e.target.value })} /></FormRow>)}

            <FormGroupDivider>Valuation Workflow</FormGroupDivider>
            <FormRow label="Cost Category" helper="Categorise the variation for reporting"><UISelect value={form.costCategory} onChange={(e) => set({ costCategory: e.target.value })}>{COST_CATEGORY_OPTIONS.map((o) => <option key={o}>{o}</option>)}</UISelect></FormRow>
            {[
              ["estimatedValue", "Estimated Value", "Initial engineer's estimate"],
              ["quotedValue", "Quoted Value", "Value quoted by the contractor"],
              ["assessedValue", "Assessed Value", "Value assessed by the engineer"],
            ].map(([key, label, helper]) => <FormRow key={key} label={label} helper={helper}><UIInput type="number" defaultValue={form[key] || ""} onBlur={(e) => set({ [key]: e.target.value })} /></FormRow>)}

            <FormRow label="Approved Value" helper="Leave blank to use the full gross amount automatically."><UIInput type="number" placeholder={String(grossVoAmount || "")} defaultValue={form.approvedValue || ""} onBlur={(e) => set({ approvedValue: e.target.value })} /></FormRow>

            <FormGroupDivider>Certification Progress</FormGroupDivider>
            {[
              ["amountPreviouslyCertified", "Amount Previously Certified", "Total certified in earlier periods"],
              ["amountCertifiedThisPeriod", "Amount Certified This Period", "Amount certified in the current period"],
            ].map(([key, label, helper]) => <FormRow key={key} label={label} helper={helper}><UIInput type="number" defaultValue={form[key] || ""} onBlur={(e) => set({ [key]: e.target.value })} /></FormRow>)}
            <FormRow label="Balance to Certify" helper="Approved value minus what has already been certified"><ReadOnlyCalc value={currency(balanceToCertify)} accent={balanceToCertify <= 0} /></FormRow>

            <FormGroupDivider>Extension of Time</FormGroupDivider>
            {[
              ["eotClaimedDays", "EOT Claimed Days", "Working days claimed by the contractor"],
              ["eotAssessedDays", "EOT Assessed Days", "Days assessed by the engineer"],
              ["eotApprovedDays", "EOT Approved Days", "Days actually granted"],
            ].map(([key, label, helper]) => <FormRow key={key} label={label} helper={helper}><UIInput type="number" defaultValue={form[key] || ""} onBlur={(e) => set({ [key]: e.target.value })} /></FormRow>)}
            <FormRow label="Critical Path Impact" helper="Does this variation affect the critical path?"><UISelect value={form.criticalPathImpact} onChange={(e) => set({ criticalPathImpact: e.target.value })}><option value="No">No</option><option value="Yes">Yes — critical path affected</option></UISelect></FormRow>
            <FormRow label="Notes" helper="Internal notes for record-keeping"><Textarea rows={3} defaultValue={form.notes} onBlur={(e) => set({ notes: e.target.value })} /></FormRow>
          </CollapseCard>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", borderTop: `1px solid ${T.color.border}`, paddingTop: 16, marginTop: 4 }}>
            <UIButton variant="accent" onClick={handleSave}> {editingVariationId ? "Update Variation Order" : "Save Variation Order"}</UIButton>
            <UIButton variant="subtle" onClick={onClose}>Cancel</UIButton>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function VariationsSection({
  projects = [],
  variationForm = {},
  setVariationForm = () => {},
  handleAddOrUpdateVariation,
  editingVariationId,
  variations = [],
  handleEditVariation,
  handleDeleteVariation,
  handleBulkUpdateVariations,
  rfis = [],
  boqSections = [],
  soqItems = [],
  certificates = [],
  handleViewItem,
  currentProjectId,
}) {
  const safeVariations = Array.isArray(variations) ? variations : [];
  const [showFormModal, setShowFormModal] = useState(false);
  const [modalInitialForm, setModalInitialForm] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [fundingFilter, setFundingFilter] = useState("All");
  const [sortField, setSortField] = useState(() => localStorage.getItem("vs_defaultSortField") || "requestDate");
  const [sortDirection, setSortDirection] = useState(() => localStorage.getItem("vs_defaultSortDirection") || "desc");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [registerCollapsed, setRegisterCollapsed] = useState(false);
  const [showSortSettings, setShowSortSettings] = useState(false);
  const [defaultSortField, setDefaultSortField] = useState(() => localStorage.getItem("vs_defaultSortField") || "requestDate");
  const [defaultSortDirection, setDefaultSortDirection] = useState(() => localStorage.getItem("vs_defaultSortDirection") || "desc");

  const fundingSummary = useMemo(() =>
    FUNDING_SOURCE_OPTIONS.map((source) => {
      const approvedVOs = safeVariations.filter((v) => (v?.fundingSource || "Contingency") === source && isApprovedVariation(v));
      return {
        source,
        count: approvedVOs.length,
        total: approvedVOs.reduce((s, v) => s + numberOrZero(v?.additionalWorkAmount) + numberOrZero(v?.existingRatesAmount), 0),
      };
    }),
    [safeVariations]
  );

  const totalApprovedNet = useMemo(() =>
    safeVariations
      .filter((v) => isApprovedVariation(v))
      .reduce((s, v) => s + numberOrZero(v?.additionalWorkAmount) + numberOrZero(v?.existingRatesAmount), 0),
    [safeVariations]
  );

  const pendingCount = useMemo(() =>
    safeVariations.filter((v) => !["Approved", "Rejected", "Cancelled", "Closed"].includes(String(v?.status || ""))).length,
    [safeVariations]
  );

  const debouncedSearch = useDebouncedValue(searchText, 180);

  const filteredVariations = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    const rows = safeVariations.filter((row) => {
      if (!row) return false;
      if (statusFilter !== "All" && String(row.status || "") !== statusFilter) return false;
      if (fundingFilter !== "All" && (row.fundingSource || "Contingency") !== fundingFilter) return false;
      if (!needle) return true;
      const hay = [row.variationNo, row.voDescription, row.title, row.reason, row.requestedBy, row.fundingSource, row.status, row.contractor, row.notes].join(" ").toLowerCase();
      return hay.includes(needle);
    });

    return [...rows].sort((a, b) => {
      let av;
      let bv;
      if (sortField === "requestDate") { av = a.requestDate || ""; bv = b.requestDate || ""; }
      else if (sortField === "variationNo") { av = a.variationNo || ""; bv = b.variationNo || ""; }
      else if (sortField === "status") { av = a.status || ""; bv = b.status || ""; }
      else if (sortField === "netVoAmount") {
        av = numberOrZero(a.additionalWorkAmount) + numberOrZero(a.existingRatesAmount) - numberOrZero(a.omittedWorkAmount);
        bv = numberOrZero(b.additionalWorkAmount) + numberOrZero(b.existingRatesAmount) - numberOrZero(b.omittedWorkAmount);
      } else if (sortField === "approvedValue") {
        av = numberOrZero(a.approvedValue);
        bv = numberOrZero(b.approvedValue);
      } else {
        av = a.voDescription || a.title || "";
        bv = b.voDescription || b.title || "";
      }
      if (typeof av === "number") return sortDirection === "asc" ? av - bv : bv - av;
      return sortDirection === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [safeVariations, statusFilter, fundingFilter, debouncedSearch, sortField, sortDirection]);

  const visibleIds = useMemo(() => filteredVariations.map((r) => r.id).filter(Boolean), [filteredVariations]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleOne = useCallback((id) => setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]), []);

  const toggleAll = useCallback(() => setSelectedIds((prev) => {
    const all = visibleIds.length > 0 && visibleIds.every((id) => prev.includes(id));
    return all ? prev.filter((id) => !visibleIds.includes(id)) : Array.from(new Set([...prev, ...visibleIds]));
  }), [visibleIds]);

  function rowGross(row) {
    return numberOrZero(row?.grossVoAmount) ||
      numberOrZero(row?.paymentCertificateVoAmount) ||
      numberOrZero(row?.additionalWorkAmount) + numberOrZero(row?.existingRatesAmount);
  }

  function rowNet(row) {
    return Math.max(0, rowGross(row) - numberOrZero(row?.omittedWorkAmount));
  }

  function rowBalance(row) {
    return rowGross(row) - numberOrZero(row?.amountPreviouslyCertified) - numberOrZero(row?.amountCertifiedThisPeriod);
  }

  function applyBulkStatus() {
    if (typeof handleBulkUpdateVariations !== "function" || !bulkStatus || !selectedIds.length) return;
    handleBulkUpdateVariations(selectedIds, selectedIds.reduce((acc, id) => {
      acc[id] = { status: bulkStatus };
      return acc;
    }, {}));
    setSelectedIds([]);
    setBulkStatus("");
  }

  function applyBulkRecalc() {
    if (typeof handleBulkUpdateVariations !== "function" || !selectedIds.length) return;
    const rows = safeVariations.filter((r) => r && selectedIds.includes(r.id));
    handleBulkUpdateVariations(selectedIds, rows.reduce((acc, row) => {
      const add = numberOrZero(row.additionalWorkAmount);
      const ex = numberOrZero(row.existingRatesAmount);
      const om = numberOrZero(row.omittedWorkAmount);
      const net = add + ex - om;
      acc[row.id] = {
        additionalWorkAmount: add,
        existingRatesAmount: ex,
        omittedWorkAmount: om,
        netVoAmount: net,
        approvedValue: numberOrZero(row.approvedValue) || net || numberOrZero(row.assessedValue) || numberOrZero(row.quotedValue) || 0,
      };
      return acc;
    }, {}));
    setSelectedIds([]);
  }

  function buildProjectDetailsForVariation(row) {
    const project = (projects || []).find((p) => String(p.id || "") === String(row?.projectId || variationForm.projectId || "")) || {};
    return {
      client: project.client || project.employer || project.owner || project.projectClient || "",
      department: project.department || project.clientDepartment || "",
      contractNumber: project.contractNumber || project.contractNo || project.contractRef || "",
      contractDescription: project.contractDescription || project.description || project.name || project.title || "",
      contractor: project.contractor || row?.contractor || variationForm.contractor || "",
    };
  }

  function handleGenerateVOProforma(row) {
    // Sign-off gate — an AI draft has no authority until a named engineer
    // issues it, so it cannot be exported as an official VO proforma.
    if (requiresSignoff(row)) {
      alert(
        `Variation ${row.variationNo || row.id} is an AI draft pending engineer sign-off. ` +
        `Sign it off first — it cannot be issued as a PDF until approved.`
      );
      return;
    }
    generateVOProformaPdf({
      variation: row,
      projectDetails: buildProjectDetailsForVariation(row),
      certificateNo: row.paymentCertificateRef || row.certificateNo || "",
    });
  }

  // ── Sign-off gate — issue an AI draft under a named engineer ────────────────
  const [signingId, setSigningId] = useState(null);
  async function onSignOff(row) {
    const pid = currentProjectId || row.projectId;
    if (!pid) return;
    if (!window.confirm(
      `Sign off variation ${row.variationNo || row.id}? ` +
      `This issues it as an official variation recorded in your name.`
    )) return;
    setSigningId(row.id);
    try {
      await approveDocument({
        projectId: pid,
        documentType: "variation",
        documentId: row.id,
      });
      // The live Firestore listener refreshes the row's approval state.
    } catch (e) {
      alert(e.message || "Sign-off failed.");
    } finally {
      setSigningId(null);
    }
  }

  function buildViewSummary(row) {
    const gross = rowGross(row);
    const net = rowNet(row);
    const items = Array.isArray(row?.omissionItems) ? row.omissionItems : [];
    return {
      "VO Number": row?.variationNo || row?.id || "—",
      "VO Description": row?.voDescription || row?.title || "—",
      "Status": row?.status || "—",
      "Funding Source": row?.fundingSource || "Contingency",
      "Requested By": row?.requestedBy || "—",
      "Date Requested": row?.requestDate || "—",
      "Additional Work": currency(row?.additionalWorkAmount),
      "Existing Rates": currency(row?.existingRatesAmount),
      "PC VO Amount (Gross)": currency(gross),
      "Omitted Work": currency(row?.omittedWorkAmount),
      "Net VO (Proforma)": currency(net),
      "Approved Value": currency(gross),
      "Balance to Certify": currency(rowBalance(row)),
      "EOT Claimed Days": row?.eotClaimedDays || "—",
      "EOT Approved Days": row?.eotApprovedDays || "—",
      "Critical Path Impact": row?.criticalPathImpact || "No",
      "Notes": row?.notes || "—",
      "Omitted Items": items.length ? items.map((e) => `${e.itemNo} | ${e.description} | ${numberOrZero(e.omitQty)} ${e.unit} | ${currency(e.omittedAmount)}`) : "None",
    };
  }

  function openFormForEdit(row) {
    if (typeof handleEditVariation === "function") handleEditVariation(row);
    setModalInitialForm({ ...emptyVariationForm, ...row });
    setShowFormModal(true);
  }

  function handleModalSave(nextForm) {
    const safeNextForm = {
      ...emptyVariationForm,
      ...(nextForm || {}),
      status: nextForm?.status || "Draft",
      omissionItems: Array.isArray(nextForm?.omissionItems) ? nextForm.omissionItems : [],
      additionalWorkAmount: String(numberOrZero(nextForm?.additionalWorkAmount)),
      existingRatesAmount: String(numberOrZero(nextForm?.existingRatesAmount)),
      grossVoAmount: String(numberOrZero(nextForm?.grossVoAmount)),
      paymentCertificateVoAmount: String(numberOrZero(nextForm?.paymentCertificateVoAmount)),
      approvedValue: String(numberOrZero(nextForm?.approvedValue)),
      omittedWorkAmount: String(numberOrZero(nextForm?.omittedWorkAmount)),
      netVoAmount: String(numberOrZero(nextForm?.netVoAmount)),
      proformaNetVoAmount: String(numberOrZero(nextForm?.proformaNetVoAmount)),
    };

    try {
      setVariationForm(safeNextForm);
      if (typeof handleAddOrUpdateVariation === "function") handleAddOrUpdateVariation(safeNextForm);
      setModalInitialForm(null);
      setShowFormModal(false);
    } catch (error) {
      console.error("Variation save failed:", error, safeNextForm);
      alert(`Variation save failed: ${error?.message || error}`);
    }
  }

  function handleModalClose() {
    setModalInitialForm(null);
    setShowFormModal(false);
  }

  return (
    <section className="variation-section-shell" style={{ fontFamily: T.font.body }}>
      <style>{scrollCSS}</style>
      <BlueprintGrid />

      <div className="variation-content-stack">
        <Reveal delay={0}>
          <UICard padded={false} style={{ borderRadius: 14, position: "relative", width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${T.color.accent} 0%, ${T.color.primary} 40%, ${T.color.primary} 100%)` }} />
            <div style={{ padding: "24px 26px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                  <Eyebrow>Commercial Change</Eyebrow>
                  <span style={{ fontFamily: T.font.mono, fontSize: 11, fontWeight: 600, color: T.color.inkMuted, letterSpacing: "0.02em" }}>{safeVariations.length} VO{safeVariations.length !== 1 ? "s" : ""} logged</span>
                  {pendingCount > 0 && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.warning }}>● {pendingCount} pending</span>}
                </div>
                <h1 style={{ margin: 0, fontFamily: T.font.display, fontSize: 26, fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.1, color: T.color.ink }}>Variation Order Register</h1>
                <p style={{ margin: "6px 0 0", fontSize: 13, color: T.color.inkMuted, lineHeight: 1.5, maxWidth: 720 }}>
                  Track every commercial change to the contract — additions, omissions, remeasurement, dayworks. Net values feed directly into the certificate and CPA totals.
                </p>
              </div>
              <UIButton variant="accent" onClick={() => { setModalInitialForm({ ...emptyVariationForm, projectId: variationForm.projectId }); setShowFormModal(true); }}>New Variation Order</UIButton>
            </div>
          </UICard>
        </Reveal>

        <Reveal delay={120}>
          <div className="variation-kpi-grid">
            <div><Stat label="Total VOs" rawValue={safeVariations.length} format={(v) => Math.round(v)} value={safeVariations.length} accent={T.color.primary} delay={80} helper="Across all statuses" /></div>
            <div><Stat label="Approved Net" rawValue={totalApprovedNet} format={(v) => currency(v)} value={currency(totalApprovedNet)} accent={T.color.success} delay={140} helper={`${safeVariations.filter((v) => isApprovedVariation(v)).length} approved`} /></div>
            <div><Stat label="Pending Approval" rawValue={pendingCount} format={(v) => Math.round(v)} value={pendingCount} accent={T.color.warning} delay={200} helper="Not yet decided" /></div>
            <div><Stat label="Rejected / Cancelled" rawValue={safeVariations.filter((v) => ["Rejected", "Cancelled"].includes(String(v?.status || ""))).length} format={(v) => Math.round(v)} value={safeVariations.filter((v) => ["Rejected", "Cancelled"].includes(String(v?.status || ""))).length} accent={T.color.danger} delay={260} helper="Will not be certified" /></div>
            <div><Stat label="Draft" rawValue={safeVariations.filter((v) => String(v?.status || "") === "Draft").length} format={(v) => Math.round(v)} value={safeVariations.filter((v) => String(v?.status || "") === "Draft").length} accent={T.color.accent} delay={320} helper="Not yet instructed" /></div>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <UICard padded={false} style={{ borderRadius: 14, width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
            <div style={{ padding: "20px 22px 4px" }}>
              <Eyebrow>Funding</Eyebrow>
              <h3 style={{ margin: "6px 0 0", fontFamily: T.font.display, fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: T.color.ink }}>Funding Source Summary</h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: T.color.inkSubtle }}>Approved net VO value grouped by where the money comes from.</p>
            </div>
            <div className="variation-responsive-grid" style={{ padding: "16px 22px 22px" }}>
              {fundingSummary.map((item) => {
                const accent = item.source === "Savings" ? T.color.success : item.source === "Section 116" ? T.color.info : T.color.primary;
                return (
                  <div key={item.source} style={{ position: "relative", background: T.color.surfaceAlt, border: `1px solid ${T.color.border}`, borderRadius: T.radius.sm, padding: "14px 16px", overflow: "hidden", minWidth: 0, boxSizing: "border-box" }}>
                    <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: 3, background: accent }} />
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>{item.source}</div>
                    <div style={{ marginTop: 6, fontFamily: T.font.mono, fontSize: 18, fontWeight: 700, color: T.color.ink, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}>{currency(item.total)}</div>
                    <div style={{ marginTop: 4, fontSize: 11, color: T.color.inkMuted }}>{item.count} approved VO{item.count !== 1 ? "s" : ""}</div>
                  </div>
                );
              })}
            </div>
          </UICard>
        </Reveal>

        <Reveal delay={260}>
          <UICard padded={false} style={{ borderRadius: 14, width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
            <div style={{ padding: "20px 22px 4px" }}>
              <Eyebrow color={T.color.info}>Bulk Operations</Eyebrow>
              <h3 style={{ margin: "6px 0 0", fontFamily: T.font.display, fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: T.color.ink }}>Bulk Update</h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: T.color.inkSubtle }}>Select rows in the register below, then apply a bulk status change or recalculate approved values.</p>
            </div>
            <div style={{ padding: "12px 22px 22px" }}>
              <div className="variation-responsive-grid">
                <div style={{ display: "grid", gap: 4 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>Selected</span>
                  <div style={{ padding: "8px 12px", borderRadius: T.radius.sm, background: selectedIds.length > 0 ? T.color.accentSoft : T.color.surfaceAlt, border: `1px solid ${selectedIds.length > 0 ? "#F5BFA3" : T.color.border}`, fontSize: 13, fontWeight: 700, color: selectedIds.length > 0 ? T.color.accent : T.color.inkMuted, height: 34, display: "flex", alignItems: "center" }}>{selectedIds.length} VO{selectedIds.length !== 1 ? "s" : ""}</div>
                </div>
                <div style={{ display: "grid", gap: 4 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>Apply Status</span>
                  <UISelect value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}><option value="">Select status…</option>{STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}</UISelect>
                </div>
                <UIButton variant="primary" onClick={applyBulkStatus} disabled={typeof handleBulkUpdateVariations !== "function" || !bulkStatus || !selectedIds.length}>Apply Status</UIButton>
                <UIButton variant="outline" onClick={applyBulkRecalc} disabled={typeof handleBulkUpdateVariations !== "function" || !selectedIds.length}>Recalculate Approved Values</UIButton>
              </div>
            </div>
          </UICard>
        </Reveal>

        {showFormModal && modalInitialForm && (
          <VOFormModal
            initialForm={modalInitialForm}
            editingVariationId={editingVariationId}
            projects={projects}
            rfis={rfis}
            boqSections={boqSections}
            soqItems={soqItems}
            variations={safeVariations}
            certificates={certificates}
            onSave={handleModalSave}
            onClose={handleModalClose}
          />
        )}

        <Reveal delay={320}>
          <UICard padded={false} style={{ borderRadius: 14, width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
            <div style={{ padding: "20px 22px 16px", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div>
                <Eyebrow>Register</Eyebrow>
                <h3 style={{ margin: "6px 0 0", fontFamily: T.font.display, fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: T.color.ink }}>Variation Orders</h3>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.color.inkSubtle }}>Showing <strong style={{ fontFamily: T.font.mono, color: T.color.ink }}>{filteredVariations.length}</strong> of <strong style={{ fontFamily: T.font.mono, color: T.color.ink }}>{safeVariations.length}</strong> records</p>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <UIButton size="sm" variant="subtle" onClick={() => setShowSortSettings((v) => !v)}>⚙ Sort Defaults</UIButton>
                <UIButton size="sm" variant="subtle" onClick={() => setRegisterCollapsed((v) => !v)}>{registerCollapsed ? "⤢ Expand" : "⤡ Roll Up"}</UIButton>
              </div>
            </div>

            {!registerCollapsed && (
              <div style={{ padding: "0 22px 22px", width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", overflow: "hidden" }}>
                {showSortSettings && (
                  <div className="variation-responsive-grid" style={{ marginBottom: 14, padding: "14px 16px", borderRadius: T.radius.sm, background: T.color.primarySoft, border: `1px solid #C0CDD8`, alignItems: "end" }}>
                    <div style={{ display: "grid", gap: 4 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.primary }}>Default Sort Field</span>
                      <UISelect value={defaultSortField} onChange={(e) => { setDefaultSortField(e.target.value); localStorage.setItem("vs_defaultSortField", e.target.value); }}>
                        {[{ v: "requestDate", l: "Date requested" }, { v: "variationNo", l: "VO number" }, { v: "description", l: "Description" }, { v: "status", l: "Status" }, { v: "netVoAmount", l: "Net VO amount" }, { v: "approvedValue", l: "Approved value" }].map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </UISelect>
                    </div>
                    <div style={{ display: "grid", gap: 4 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.primary }}>Default Direction</span>
                      <UISelect value={defaultSortDirection} onChange={(e) => { setDefaultSortDirection(e.target.value); localStorage.setItem("vs_defaultSortDirection", e.target.value); }}><option value="asc">Ascending</option><option value="desc">Descending</option></UISelect>
                    </div>
                    <UIButton variant="primary" size="sm" onClick={() => { setSortField(defaultSortField); setSortDirection(defaultSortDirection); setShowSortSettings(false); }}>Apply Default</UIButton>
                    <UIButton variant="subtle" size="sm" onClick={() => setShowSortSettings(false)}>Close</UIButton>
                  </div>
                )}

                <div className="variation-responsive-grid" style={{ marginBottom: 16 }}>
                  <div style={{ display: "grid", gap: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>Search</span>
                    <UIInput placeholder="VO number, description, contractor…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                  </div>
                  {[
                    { label: "Status", value: statusFilter, onChange: setStatusFilter, options: [{ v: "All", l: "All statuses" }, ...STATUS_OPTIONS.map((o) => ({ v: o, l: o }))] },
                    { label: "Funding", value: fundingFilter, onChange: setFundingFilter, options: [{ v: "All", l: "All sources" }, ...FUNDING_SOURCE_OPTIONS.map((o) => ({ v: o, l: o }))] },
                    { label: "Sort By", value: sortField, onChange: setSortField, options: [{ v: "requestDate", l: "Date requested" }, { v: "variationNo", l: "VO number" }, { v: "description", l: "Description" }, { v: "status", l: "Status" }, { v: "netVoAmount", l: "Net VO amount" }, { v: "approvedValue", l: "Approved value" }] },
                    { label: "Direction", value: sortDirection, onChange: setSortDirection, options: [{ v: "asc", l: "Ascending" }, { v: "desc", l: "Descending" }] },
                  ].map((f) => (
                    <div key={f.label} style={{ display: "grid", gap: 4 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>{f.label}</span>
                      <UISelect value={f.value} onChange={(e) => f.onChange(e.target.value)}>{f.options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}</UISelect>
                    </div>
                  ))}
                </div>

                {filteredVariations.length === 0 ? (
                  <Empty title="No variation orders match the filter" message="Adjust the status / funding / search filters above, or create a new VO from the page header." />
                ) : (
                  <div className="variation-table-scroll">
                    <div className="variation-table-inner">
                      <UITable>
                        <thead>
                          <tr>
                            <UITh style={{ width: 36 }}><UICheckbox checked={allSelected} onChange={toggleAll} /></UITh>
                            <UITh align="left" style={{ minWidth: 210 }}>Actions</UITh>
                            <UITh>VO No</UITh>
                            <UITh>Date</UITh>
                            <UITh>Description</UITh>
                            <UITh>Reason</UITh>
                            <UITh>Requested By</UITh>
                            <UITh>Funding</UITh>
                            <UITh>BOQ Section</UITh>
                            <UITh align="right">+ Days</UITh>
                            <UITh align="right">PC VO Amount</UITh>
                            <UITh align="right">Net Proforma</UITh>
                            <UITh align="right">Approved</UITh>
                            <UITh align="right">Certified</UITh>
                            <UITh align="right">Balance</UITh>
                            <UITh>Status</UITh>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredVariations.map((row) => {
                            const gross = rowGross(row);
                            const net = rowNet(row);
                            const breakdown = getCertifiedBreakdownForVariation(row, certificates);
                            const certified = rowTotalCertified(row, certificates);
                            const balanceRaw = gross - certified;
                            const balanceDisplay = Math.max(0, balanceRaw);
                            const fundingVariant = row.fundingSource === "Savings" ? "success" : row.fundingSource === "Section 116" ? "info" : "primary";

                            return (
                              <tr key={row.id || row.variationNo}>
                                <UITd><UICheckbox checked={selectedIds.includes(row.id)} onChange={() => toggleOne(row.id)} /></UITd>
                                <UITd align="left" style={{ minWidth: 210 }}>
                                  <div style={{ display: "inline-flex", gap: 5, flexWrap: "wrap", justifyContent: "flex-start" }}>
                                    {requiresSignoff(row) && (
                                      <UIButton size="sm" variant="accent" disabled={signingId === row.id} onClick={() => onSignOff(row)}>
                                        {signingId === row.id ? "Signing…" : "Sign off"}
                                      </UIButton>
                                    )}
                                    {handleViewItem && <UIButton size="sm" variant="success" onClick={() => handleViewItem(`Variation ${row.variationNo}`, buildViewSummary(row))}>View</UIButton>}
                                    <UIButton size="sm" variant="subtle" onClick={() => handleGenerateVOProforma(row)}>PDF</UIButton>
                                    <UIButton size="sm" variant="subtle" onClick={() => setHistoryRow(row)}>History</UIButton>
                                    <UIButton size="sm" variant="subtle" onClick={() => openFormForEdit(row)}>Edit</UIButton>
                                    <UIButton size="sm" variant="danger" onClick={() => handleDeleteVariation(row.id)}>Delete</UIButton>
                                  </div>
                                </UITd>
                                <UITd mono weight={700}>{row.variationNo || "—"}</UITd>
                                <UITd mono>{row.requestDate || <span style={{ color: T.color.inkSubtle }}>—</span>}</UITd>
                                <UITd><div style={{ fontWeight: 500 }}>{row.voDescription || row.title || "—"}</div></UITd>
                                <UITd>{row.reason ? <span style={{ color: T.color.inkMuted, fontSize: 12 }}>{row.reason}</span> : <span style={{ color: T.color.inkSubtle }}>—</span>}</UITd>
                                <UITd>{row.requestedBy || <span style={{ color: T.color.inkSubtle }}>—</span>}</UITd>
                                <UITd><UIBadge variant={fundingVariant} size="sm">{row.fundingSource || "Contingency"}</UIBadge></UITd>
                                <UITd mono>{row.boqSectionLabel || row.boqSectionReference || row.boqSection || <span style={{ color: T.color.inkSubtle }}>—</span>}</UITd>
                                <UITd align="right" mono>{row.estimatedAdditionalDays || 0}</UITd>
                                <UITd align="right" mono weight={600}>{currency(gross)}</UITd>
                                <UITd align="right" mono>{currency(net)}</UITd>
                                <UITd align="right" mono weight={600}>{currency(gross)}</UITd>
                                <UITd align="right"><div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}><span style={{ fontFamily: T.font.mono, fontWeight: certified > 0 ? 700 : 500, color: certified > 0 ? T.color.success : T.color.inkSubtle }}>{certified > 0 ? currency(certified) : "—"}</span><CertifiedInfoPopover breakdown={breakdown} /></div></UITd>
                                <UITd align="right" mono weight={balanceDisplay === 0 && gross > 0 ? 700 : 500} style={{ color: balanceRaw < 0 ? T.color.danger : balanceDisplay === 0 && gross > 0 ? T.color.success : T.color.ink }}>{currency(balanceDisplay)}</UITd>
                                <UITd>
                                  {(() => {
                                    const ab = approvalBadge(row);
                                    return (
                                      <div style={{ display: "inline-flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                                        {ab && <UIBadge variant={ab.variant} size="sm">{ab.label}</UIBadge>}
                                        <UIBadge variant={statusBadgeVariant(row.status)} size="sm">{row.status || "—"}</UIBadge>
                                      </div>
                                    );
                                  })()}
                                </UITd>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          {(() => {
                            const approvedRows = filteredVariations.filter((r) => isApprovedVariation(r));
                            const totalGross = filteredVariations.reduce((s, r) => s + rowGross(r), 0);
                            const totalNet = filteredVariations.reduce((s, r) => s + rowNet(r), 0);
                            const totalApproved = approvedRows.reduce((s, r) => s + rowGross(r), 0);
                            const totalCertified = filteredVariations.reduce((s, r) => s + rowTotalCertified(r, certificates), 0);
                            const totalBalance = filteredVariations.reduce((s, r) => s + Math.max(0, rowGross(r) - rowTotalCertified(r, certificates)), 0);
                            const totalDays = filteredVariations.reduce((s, r) => s + numberOrZero(r.estimatedAdditionalDays), 0);
                            const baseCell = { padding: "12px 12px", fontSize: 12, fontWeight: 700, borderTop: `2px solid ${T.color.borderStrong}`, background: T.color.surfaceAlt, fontFamily: T.font.mono, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" };

                            return (
                              <tr>
                                <td style={baseCell} />
                                <td style={{ ...baseCell, fontFamily: T.font.body, textAlign: "left", whiteSpace: "nowrap" }} colSpan={8}>TOTALS — {filteredVariations.length} VO{filteredVariations.length !== 1 ? "s" : ""} <span style={{ color: T.color.inkSubtle, fontWeight: 500, fontSize: 11, marginLeft: 8 }}>({approvedRows.length} approved · current filter)</span></td>
                                <td style={{ ...baseCell, textAlign: "right" }}>{totalDays}</td>
                                <td style={{ ...baseCell, textAlign: "right" }}>{currency(totalGross)}</td>
                                <td style={{ ...baseCell, textAlign: "right" }}>{currency(totalNet)}</td>
                                <td style={{ ...baseCell, textAlign: "right", color: T.color.success }}>{currency(totalApproved)}</td>
                                <td style={{ ...baseCell, textAlign: "right", color: totalCertified > 0 ? T.color.success : T.color.inkSubtle }}>{totalCertified > 0 ? currency(totalCertified) : "—"}</td>
                                <td style={{ ...baseCell, textAlign: "right", color: T.color.accent }}>{currency(totalBalance)}</td>
                                <td style={baseCell} />
                                <td style={baseCell} />
                              </tr>
                            );
                          })()}
                        </tfoot>
                      </UITable>
                    </div>
                  </div>
                )}
              </div>
            )}
          </UICard>
        </Reveal>
      </div>

      {historyRow && (
        <VersionHistory
          projectId={currentProjectId}
          collection="variations"
          docId={historyRow.id}
          label={`VO ${historyRow.variationNo || historyRow.id}`}
          onClose={() => setHistoryRow(null)}
        />
      )}
    </section>
  );
}

export default React.memo(VariationsSection);